import React from 'react';
import { Box, Stack, Text, NumberInput, NumberInputField } from '@chakra-ui/react';
import { colors } from '../../utils/colors';
import { POINTS_PROFILES, getEaseLevel, getProfileWeeksLabel } from '../../utils/pointsEase';

const PLACEHOLDER_COLOR = 'rgba(255,255,255,0.2)';

interface BikletaPointsCostProps {
  value?: number | null;
  onChange: (value: number | null) => void;
  error?: string;
}

const BikletaPointsCost: React.FC<BikletaPointsCostProps> = ({
  value,
  onChange,
  error,
}) => {
  const pointsCost = value ?? 0;
  const hasValue = pointsCost > 0;
  const ease = hasValue ? getEaseLevel(pointsCost) : null;

  return (
    <Box
      bg={'rgba(247, 212, 45, 0.06)'}
      border={'0.5px solid rgba(247, 212, 45, 0.25)'}
      borderRadius={10}
      p={5}>
      <Stack direction={'row'} alignItems={'center'} spacing={2} mb={4}>
        <Box
          w={'26px'}
          h={'26px'}
          borderRadius={'50%'}
          bg={colors.accent.main}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          fontSize={12}
          fontWeight={700}
          color={'#0a0a0a'}
          flexShrink={0}>
          B
        </Box>
        <Text fontSize={14} fontWeight={500} color={colors.accent.main}>
          Costo en Bikleta Points
        </Text>
      </Stack>

      <Stack direction={'row'} alignItems={'center'} spacing={3} mb={2}>
        <NumberInput
          value={value ?? ''}
          min={0}
          step={50}
          onChange={valueString => {
            if (valueString === '') {
              onChange(null);
              return;
            }
            const parsed = Number(valueString);
            onChange(isNaN(parsed) ? null : parsed);
          }}
          w={'140px'}
          flexShrink={0}>
          <NumberInputField
            placeholder={'0'}
            fontSize={22}
            fontWeight={500}
            bg={'rgba(255,255,255,0.08)'}
            borderColor={'rgba(247,212,45,0.35)'}
            _focus={{ borderColor: colors.accent.main }}
          />
        </NumberInput>
        <Text fontSize={13} color={'rgba(255,255,255,0.35)'} lineHeight={1.4}>
          BP para canjear
          <br />
          este beneficio
        </Text>
      </Stack>

      {error && (
        <Text fontSize={12} color={colors.red.main} mb={2}>
          {error}
        </Text>
      )}

      <Box mb={3.5} mt={4}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'baseline'} mb={2}>
          <Text
            fontSize={11}
            color={'rgba(255,255,255,0.4)'}
            textTransform={'uppercase'}
            letterSpacing={'0.5px'}>
            ¿Qué tan fácil es para el usuario?
          </Text>
          <Text fontSize={14} fontWeight={500} color={ease?.color ?? PLACEHOLDER_COLOR}>
            {ease?.label ?? '—'}
          </Text>
        </Stack>
        <Box bg={'rgba(255,255,255,0.08)'} borderRadius={20} h={'8px'} overflow={'hidden'}>
          <Box
            h={'100%'}
            borderRadius={20}
            transition={'width 0.35s ease, background 0.35s ease'}
            w={`${ease?.pct ?? 0}%`}
            bg={ease?.color ?? 'transparent'}
          />
        </Box>
      </Box>

      <Stack direction={'row'} spacing={2} mt={3.5}>
        {POINTS_PROFILES.map(profile => {
          const label = hasValue
            ? getProfileWeeksLabel(pointsCost, profile)
            : { text: '—', color: PLACEHOLDER_COLOR };

          return (
            <Box
              key={profile.id}
              flex={1}
              bg={'rgba(255,255,255,0.05)'}
              borderRadius={8}
              px={2}
              py={2.5}
              textAlign={'center'}>
              <Text fontSize={16} mb={1}>
                {profile.emoji}
              </Text>
              <Text
                fontSize={10}
                color={'rgba(255,255,255,0.35)'}
                textTransform={'uppercase'}
                letterSpacing={'0.4px'}
                mb={1.5}>
                {profile.label}
              </Text>
              <Text fontSize={15} fontWeight={500} color={label.color}>
                {label.text}
              </Text>
              {hasValue && (
                <Text fontSize={10} color={'rgba(255,255,255,0.3)'} mt={0.5}>
                  pedalear
                </Text>
              )}
            </Box>
          );
        })}
      </Stack>

      <Text fontSize={11} color={'rgba(255,255,255,0.25)'} lineHeight={1.5} mt={3}>
        {ease?.hint ?? 'Ingresá el costo en puntos para ver qué tan accesible es este beneficio.'}
      </Text>
    </Box>
  );
};

export default BikletaPointsCost;
